"use client";

import React from "react";
import { Loader2 } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GetSupport() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="get-support" className="bg-background">
      <div className="relative min-h-screen overflow-hidden bg-primary py-24 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
        {}
        <div className="absolute top-0 left-0 w-[40vw] h-[40vw] min-w-[300px] min-h-[300px] bg-secondary/15 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2 pointer-events-none animate-pulse" style={{
        animationDuration: '8s'
      }} />
        <div className="absolute bottom-0 right-0 w-[50vw] h-[50vw] min-w-[400px] min-h-[400px] bg-ring/10 rounded-full blur-[120px] translate-x-1/3 translate-y-1/3 pointer-events-none animate-pulse" style={{
        animationDuration: '12s',
        animationDelay: '2s'
      }} />

        <div ref={ref} className={`relative z-10 w-full max-w-3xl mx-auto transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="text-center mb-12">
            <h2 className="font-serif italic text-ring text-4xl md:text-5xl lg:text-6xl mb-6 tracking-tight">
              Get Support
            </h2>
            <p className="text-primary-foreground/80 text-lg md:text-xl max-w-2xl mx-auto font-light">
              We are here to help you navigate your journey. Please fill out the form below, and our team will connect with you shortly.
            </p>
          </div>

          <div className="bg-card/5 backdrop-blur-md border border-white/10 rounded-[2.5rem] p-6 sm:p-10 md:p-12 shadow-2xl shadow-black/40 relative overflow-hidden">
            {}
            <div className="absolute inset-0 shadow-[inset_0_0_50px_rgba(255,255,255,0.05)] pointer-events-none rounded-[2.5rem]" />
            
            <FormProvider formId="support_form_caregivercorner" sectionName="Get Support" defaultValues={{
            name: "",
            email: "",
            phone: "",
            relationship: "",
            child_age: "",
            support_area: [],
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: false
            },
            relationship: {
              required: true
            },
            child_age: {
              required: true,
              min: 0,
              max: 18
            },
            support_area: {
              required: true
            },
            message: {
              required: false
            }
          }} submitText="Submit Request" successMessage="Thank you for reaching out. Your request has been received and we will be in touch soon.">
              <SupportForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function SupportForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in duration-500">
        <div className="size-16 bg-ring/20 text-ring rounded-full flex items-center justify-center mb-6">
          <svg className="size-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-2xl font-serif italic text-primary-foreground mb-4">Request Received</h3>
        <p className="text-primary-foreground/80 text-lg max-w-md">{successMessage}</p>
      </div>;
  }
  const inputClasses = "bg-secondary/10 border-secondary/20 text-primary-foreground placeholder:text-primary-foreground/40 focus-visible:bg-primary focus-visible:border-ring focus-visible:ring-ring/50 h-12 rounded-xl transition-all duration-300";
  const labelClasses = "text-primary-foreground/90 font-medium ml-1";
  return <Form onSubmit={handleSubmit} className="space-y-6 relative z-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className={inputClasses} {...field} />
              </FormControl>
              <FormDescription className="text-primary-foreground/50 ml-1">First and last name</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className={inputClasses} {...field} />
              </FormControl>
              <FormDescription className="text-primary-foreground/50 ml-1">Optional – we may call you for follow-up</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="child_age" rules={{
        required: "Child's age is required",
        min: {
          value: 0,
          message: "Age cannot be negative"
        },
        max: {
          value: 18,
          message: "Age must be 18 or under"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Child's Age (years)</FormLabel>
              <FormControl>
                <Input type="number" placeholder="e.g., 7" min="0" max="18" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="relationship" rules={{
      required: "Please select your relationship"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClasses}>Your Relationship to the Child</FormLabel>
            <FormControl>
              <div className="relative">
                <select className={`w-full appearance-none px-3 py-2 text-base md:text-sm shadow-sm outline-none ${inputClasses}`} {...field}>
                  <option value="" disabled className="text-primary bg-background">Select your relationship</option>
                  {["Parent", "Grandparent", "Legal Guardian", "Foster Parent", "Other"].map((opt, i) => <option key={i} value={opt} data-index={i} className="text-primary bg-background">
                      {opt}
                    </option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-primary-foreground/50">
                  <svg className="size-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="support_area" rules={{
      required: "Please select at least one area of support"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClasses}>Area Where You Need Support</FormLabel>
            <FormControl>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 bg-secondary/5 p-4 rounded-xl border border-secondary/10">
                {["Storytelling & Writing Coaching", "Art & Movement Workshops", "Therapeutic Video Resources", "Weekend/Day Program Information", "Connecting with Clinicians", "Other"].map((opt, i) => <label key={i} data-index={i} className="flex items-start gap-3 cursor-pointer group p-2 rounded-lg hover:bg-secondary/10 transition-colors">
                    <div className="relative flex items-center justify-center mt-0.5">
                      <input type="checkbox" value={opt} checked={Array.isArray(field.value) && field.value.includes(opt)} onChange={e => {
                const current = Array.isArray(field.value) ? field.value : [];
                const updated = e.target.checked ? [...current, opt] : current.filter((val: string) => val !== opt);
                field.onChange(updated);
              }} className="peer appearance-none size-5 rounded border-2 border-secondary/30 bg-transparent checked:bg-ring checked:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-primary transition-all cursor-pointer" />
                      <svg className="absolute size-3.5 text-primary-foreground opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm text-primary-foreground/80 group-hover:text-primary-foreground transition-colors leading-tight pt-0.5">
                      {opt}
                    </span>
                  </label>)}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClasses}>Additional Details / Questions</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us more about how we can help..." className={`min-h-[120px] resize-y ${inputClasses}`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/20 border border-destructive/50 rounded-xl backdrop-blur-sm">
          <p className="text-destructive-foreground text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-full bg-ring text-primary-foreground hover:bg-ring/90 hover:scale-[1.02] transition-all duration-300 text-lg h-14 mt-8 shadow-lg shadow-ring/20 font-medium">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 size-5" />
            Submitting Request...
          </> : 'Submit Request'}
      </Button>
    </Form>;
}